// hazardous-chemicals/pages/ChemicalDetailPage.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../../../context/AuthContext';
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../../../constants/permissions.constants';
import { ArrowLeft, Trash2, Pencil, FlaskConical, Upload, FileText, History, ClipboardList, Loader2, AlertTriangle, Download, Archive } from 'lucide-react';
import { InfoCell, SectionCard } from '../components/shared';
import { GhsPill, ReviewDueTag, fmtDate, isReviewDue } from '../utils/safety-utils';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../../components/ui/tabs';
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogCancel,
} from '../../../../components/ui/alert-dialog';
import { Button } from '../../../../components/ui/button';
import * as chemApi from '../../../../utils/safety-api';
import type { Chemical, ChemicalLists } from '../../../../types/chemical.types';

const DEFAULT_LISTS: ChemicalLists = {
    ghsSignal: ['Danger', 'Warning', 'None'],
    ppeOptions: ['Gloves', 'Safety Goggles', 'Respirator / P2 Mask', 'Face Shield', 'Hi-Vis Vest', 'Safety Boots', 'Hard Hat', 'Hearing Protection', 'Full Body Suit'],
    locations: ['Chemical Store A', 'Fuel Store', 'Workshop', 'Materials Yard', 'Gas Store', 'Head Office'],
};

export const ChemicalDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const currentUserName = currentUser ? (currentUser.name || currentUser.email || 'Unknown') : 'Unknown';
    const ability = useAbility(AbilityContext);
    const canUpdate = ability.can(ACTIONS.UPDATE, RESOURCES.HAZARDOUS_CHEMICAL);
    const canDelete = ability.can(ACTIONS.DELETE, RESOURCES.HAZARDOUS_CHEMICAL);

    const [chemical, setChemical] = useState<Chemical | null>(null);
    const [lists, setLists] = useState<ChemicalLists>(DEFAULT_LISTS);
    const [activeTab, setActiveTab] = useState('details');
    const [archiveModalOpen, setArchiveModalOpen] = useState(false);
    const [archiveLoading, setArchiveLoading] = useState(false);
    const [uploadingSds, setUploadingSds] = useState(false);
    const [loading, setLoading] = useState(true);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!id) return;
        (async () => {
            try {
                setLoading(true);
                const [c, settings] = await Promise.all([
                    chemApi.getChemical(id),
                    chemApi.getChemicalSettings().catch(() => null),
                ]);
                setChemical(c);
                if (settings) setLists(settings);
            } catch {
                toast.error('Could not load chemical details');
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);

    const softDelete = async () => {
        if (!chemical || !id) return;
        setArchiveLoading(true);
        try {
            await chemApi.softDeleteChemical(id);
            toast.success(`${chemical.name} archived`);
            setArchiveModalOpen(false);
            navigate('/safety/hazardous-chemicals');
        } catch {
            toast.error('Could not archive chemical');
        } finally {
            setArchiveLoading(false);
        }
    };

    const handleEdit = () => {
        if (!chemical) return;
        navigate('/safety/hazardous-chemicals', { state: { editChemical: chemical } });
    };

    const handleDownloadSds = async () => {
        if (!chemical?.sds?.s3Key && !chemical?.sds?.url) {
            toast.error('SDS file is not available for download yet');
            return;
        }
        try {
            // If we already have a direct URL, use it (for backwards compat)
            if (chemical.sds.url && !chemical.sds.s3Key) {
                const link = document.createElement('a');
                link.href = chemical.sds.url;
                link.download = chemical.sds.fileName;
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                return;
            }
            // Otherwise fetch a fresh signed URL
            const { downloadUrl, fileName } = await chemApi.getSdsDownloadUrl(chemical.id);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = fileName || chemical.sds.fileName;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err: any) {
            toast.error(err.message || 'Failed to generate download link');
        }
    };

    const handleUploadSds = () => {
        fileInputRef.current?.click();
    };

    const handleSdsFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !id) return;

        // Validate file type - only PDF allowed for SDS
        if (!file.type.includes('pdf')) {
            toast.error('SDS files must be in PDF format');
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        // Validate file size - max 20MB
        const maxSize = 20 * 1024 * 1024;
        if (file.size > maxSize) {
            toast.error('File size too large. Maximum size is 20MB.');
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        setUploadingSds(true);
        try {
            const sds = await chemApi.uploadSds(id, file);
            setChemical((prev) => prev ? { ...prev, sds } : prev);
            toast.success('SDS uploaded successfully');
        } catch (err: any) {
            toast.error(err.message || 'Failed to upload SDS');
        } finally {
            setUploadingSds(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10 px-0 sm:px-4 lg:px-0">
                <div className="max-w-[1500px] mx-auto w-full p-8 text-center">
                    <div className="animate-pulse h-6 w-48 bg-muted rounded mx-auto" />
                </div>
            </div>
        );
    }

    if (!chemical) {
        return (
            <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10 px-0 sm:px-4 lg:px-0">
                <div className="max-w-[1500px] mx-auto w-full p-8 text-center">
                    <h1 className="text-xl font-medium text-muted-foreground">Chemical not found</h1>
                    <button
                        onClick={() => navigate('/safety/hazardous-chemicals')}
                        className="mt-4 h-10 px-4 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent inline-flex items-center gap-2"
                    >
                        <ArrowLeft size={16} />
                        Back to Register
                    </button>
                </div>
            </div>
        );
    }

    const c = chemical;

    return (
        <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10 px-0 sm:px-4 lg:px-0">
            <div className="max-w-[1500px] mx-auto w-full">
                {/* Top navigation + actions bar */}
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <button
                            onClick={() => navigate('/safety/hazardous-chemicals')}
                            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-3"
                        >
                            <ArrowLeft size={16} />
                            Back to Register
                        </button>
                        <div className="flex items-center gap-2">
                            <FlaskConical size={20} className="text-muted-foreground" />
                            <GhsPill signal={c.ghsSignal} />
                            {c.dangerousGoods && (
                                <span className="text-[10px] font-semibold text-destructive bg-destructive/10 px-1.5 py-0.5 rounded">DG</span>
                            )}
                            {c.deleted && (
                                <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">Archived</span>
                            )}
                        </div>
                        <h1 className="text-2xl font-semibold mt-1">{c.name}</h1>
                        <p className="text-sm text-muted-foreground mt-0.5">
                            {c.location || '—'} · {c.owner || '—'}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {canDelete && !c.deleted && (
                            <button
                                onClick={() => setArchiveModalOpen(true)}
                                className="h-10 px-4 rounded-lg border border-border text-sm font-medium hover:text-destructive hover:border-destructive/30 inline-flex items-center gap-1.5"
                            >
                                <Archive size={16} />
                                Archive
                            </button>
                        )}
                        {canUpdate && c.deleted && (
                            <button
                                onClick={async () => {
                                    try {
                                        await chemApi.restoreChemical(c.id);
                                        toast.success(`${c.name} restored`);
                                        setChemical((prev) => prev ? { ...prev, deleted: false } : prev);
                                    } catch {
                                        toast.error('Could not restore chemical');
                                    }
                                }}
                                className="h-10 px-4 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 inline-flex items-center gap-1.5"
                            >
                                Restore
                            </button>
                        )}
                        {canUpdate && (
                            <button
                                onClick={handleEdit}
                                className="h-10 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 inline-flex items-center gap-1.5"
                            >
                                <Pencil size={16} />
                                Edit
                            </button>
                        )}
                    </div>
                </div>

                {/* Tabs — using same style as RecruitmentDashboard */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="bg-gray-100 p-1 rounded-full w-fit hidden md:flex h-auto mb-6">
                        <TabsTrigger
                            value="details"
                            className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all inline-flex items-center gap-2"
                        >
                            <ClipboardList size={16} />
                            Details
                        </TabsTrigger>
                        <TabsTrigger
                            value="activity-logs"
                            className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all inline-flex items-center gap-2"
                        >
                            <History size={16} />
                            Activity Logs
                        </TabsTrigger>
                    </TabsList>

                    {/* Mobile tabs (visible below md breakpoint) */}
                    <TabsList className="bg-gray-100 p-1 rounded-full w-fit flex md:hidden h-auto mb-6">
                        <TabsTrigger
                            value="details"
                            className="rounded-full px-4 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all inline-flex items-center gap-2"
                        >
                            <ClipboardList size={14} />
                            Details
                        </TabsTrigger>
                        <TabsTrigger
                            value="activity-logs"
                            className="rounded-full px-4 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all inline-flex items-center gap-2"
                        >
                            <History size={14} />
                            Activity Logs
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="details" className="mt-0 space-y-6">
                        {/* Overview section */}
                        <div className="bg-card border border-border rounded-lg p-6">
                            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-4">Overview</h2>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                <InfoCell label="Quantity">{c.quantity}</InfoCell>
                                <InfoCell label="UN Number">{c.unNumber || 'N/A'}</InfoCell>
                                <InfoCell label="Poison Schedule / pH">{c.poisonSchedule || 'N/A'}</InfoCell>
                                <InfoCell label="GHS Signal Word">{c.ghsSignal}</InfoCell>
                                <InfoCell label="Intended Use">{c.intendedUse}</InfoCell>
                                <InfoCell label="Location">{c.location}</InfoCell>
                                <InfoCell label="Owner">{c.owner}</InfoCell>
                                <InfoCell label="Last Review Date">
                                    <span className="inline-flex items-center gap-2">
                                        {fmtDate(c.lastReview)}
                                        {isReviewDue(c.lastReview) && <ReviewDueTag />}
                                    </span>
                                </InfoCell>
                                <InfoCell label="Dangerous Goods">{c.dangerousGoods ? 'Yes' : 'No'}</InfoCell>
                            </div>
                        </div>

                        {/* Safety Data Sheet section */}
                        <div className="bg-card border border-border rounded-lg p-6">
                            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-4">Safety Data Sheet</h2>
                            {uploadingSds ? (
                                <div className="rounded-lg border border-dashed border-border py-10 flex flex-col items-center text-center bg-muted/20">
                                    <Loader2 size={20} className="text-muted-foreground mb-2 animate-spin" />
                                    <p className="text-sm text-muted-foreground">Uploading SDS...</p>
                                </div>
                            ) : c.sds ? (
                                <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-4 flex items-center gap-3">
                                    <div className="h-11 w-11 rounded-lg bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
                                        <FileText size={20} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium truncate">{c.sds.fileName}</div>
                                        <div className="text-xs text-muted-foreground">
                                            Uploaded {fmtDate(c.sds.uploadedAt)} · PDF · {c.sds.sizeLabel}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={handleDownloadSds}
                                            className="h-8 px-3 rounded-lg border border-emerald-300 bg-emerald-50 text-xs font-medium text-emerald-700 hover:bg-emerald-100 transition-colors inline-flex items-center gap-1.5"
                                            title="Download SDS"
                                        >
                                            <Download size={14} />
                                            Download
                                        </button>
                                        {canUpdate && (
                                            <button
                                                onClick={handleUploadSds}
                                                className="h-8 px-3 rounded-lg border border-border text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors inline-flex items-center gap-1.5"
                                            >
                                                <Upload size={14} />
                                                Replace
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="rounded-lg border border-dashed border-border py-10 flex flex-col items-center text-center bg-muted/20">
                                    <Upload size={20} className="text-muted-foreground mb-2" />
                                    <p className="text-sm text-muted-foreground">No Safety Data Sheet attached.</p>
                                    {canUpdate && (
                                        <button
                                            onClick={handleUploadSds}
                                            className="mt-3 inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
                                        >
                                            <Upload size={16} />
                                            Upload SDS
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Storage & PPE — two columns on desktop */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-card border border-border rounded-lg p-6">
                                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-4">Storage Requirements</h2>
                                <SectionCard title="">{c.storage || '—'}</SectionCard>
                            </div>
                            <div className="bg-card border border-border rounded-lg p-6">
                                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-4">PPE Requirements</h2>
                                <SectionCard title="">{c.ppeRequired || '—'}</SectionCard>
                            </div>
                        </div>

                    </TabsContent>

                    <TabsContent value="activity-logs" className="mt-0">
                        <div className="bg-card border border-border rounded-lg p-6">
                            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-4">Activity Logs</h2>
                            <div className="space-y-3">
                                {c.audit.length === 0 && <p className="text-sm text-muted-foreground">No activity logs yet.</p>}
                                {c.audit.map((a) => (
                                    <div key={a.id} className="rounded-lg bg-muted/40 p-3.5 flex items-start justify-between">
                                        <div className="flex items-start gap-3">
                                            <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[10px] font-medium flex-shrink-0">
                                                {a.who.split(' ').map((w) => w[0]).slice(0, 2).join('')}
                                            </div>
                                            <div>
                                                <div className="text-sm font-medium">{a.action}</div>
                                                <div className="text-xs text-muted-foreground">{a.who}</div>
                                            </div>
                                        </div>
                                        <span className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(a.date)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </TabsContent>
                </Tabs>
            </div>

            {/* Archive Confirmation Modal */}
            <AlertDialog open={archiveModalOpen && !!chemical} onOpenChange={(open) => { if (!open) setArchiveModalOpen(false); }}>
                <AlertDialogContent className="sm:max-w-[400px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
                    <AlertDialogHeader className="p-6 pb-4">
                        <div className="flex items-center gap-2">
                            <AlertTriangle size={20} className="text-red-500" />
                            <AlertDialogTitle className="text-lg font-bold text-gray-900">Archive Chemical</AlertDialogTitle>
                        </div>
                        <AlertDialogDescription className="text-sm text-gray-500 font-medium pt-1">
                            Are you sure you want to archive <span className="font-bold text-gray-700">"{c.name}"</span>? This will move it to the Archived tab. It can be restored at any time for WHS record-keeping.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="px-6 pb-6 gap-2">
                        <AlertDialogCancel
                            className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
                            disabled={archiveLoading}
                        >
                            Cancel
                        </AlertDialogCancel>
                        <Button
                            className="bg-red-600 hover:bg-red-700 text-white h-10 rounded-xl text-sm font-bold px-5"
                            onClick={softDelete}
                            disabled={archiveLoading}
                        >
                            {archiveLoading ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : (
                                'Archive Chemical'
                            )}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Hidden file input for SDS upload */}
            <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                className="hidden"
                onChange={handleSdsFileChange}
            />
        </div>
    );
};

export default ChemicalDetailPage;
